// Enterprise Certification Report — Fase 14.10
import { getSafetySnapshot }            from '@/core/safety/operationalSafetyLayer'
import { getOrchestratorHealth }        from '@/core/orchestration/runtimeOrchestrator'
import { getLastLeakReport }            from '@/core/leak/memoryLeakDetector'
import { getChannelSnapshot }           from '@/core/leak/channelMonitor'
import { getLastHealthSnapshot }        from '@/core/runtimeHealthMonitor'
import { runIntegrityCheck }            from '@/core/storeIntegrityValidator'
import { validateEnterpriseDeployment } from '@/core/deployment/validateEnterpriseDeployment'
import { STORES, ROUTES }               from '@/core/governance/architectureManifest'
import { runtimeTopologyReport }        from '@/core/governance/runtimeTopologyReport'
import { useTVHealthStore }             from '@/store/tvHealthStore'

export type CertificationLevel = 'DEV' | 'PILOT READY' | 'ENTERPRISE READY' | 'MISSION CRITICAL READY'
export type AuditSeverity = 'pass' | 'warn' | 'fail'
export interface AuditFinding { severity: AuditSeverity; message: string }
export interface AuditDimension { id: string; name: string; score: number; level: AuditSeverity; findings: AuditFinding[] }
export interface EnterpriseCertificationReport { timestamp: string; overallScore: number; certificationLevel: CertificationLevel; dimensions: AuditDimension[]; criticalFindings: AuditFinding[]; warnings: AuditFinding[]; summary: string }

function dim(id: string, name: string, score: number, findings: AuditFinding[]): AuditDimension {
  const fails = findings.filter((f) => f.severity === 'fail').length
  const warns = findings.filter((f) => f.severity === 'warn').length
  return { id, name, score, level: fails > 0 ? 'fail' : warns > 0 ? 'warn' : 'pass', findings }
}
const pass = (msg: string): AuditFinding => ({ severity: 'pass', message: msg })
const warn = (msg: string): AuditFinding => ({ severity: 'warn', message: msg })
const fail = (msg: string): AuditFinding => ({ severity: 'fail', message: msg })

function auditRuntimeSafety(): AuditDimension {
  const safety = getSafetySnapshot(); const orch = getOrchestratorHealth()
  const f: AuditFinding[] = []; let score = 100
  if (safety.safeMode) { f.push(warn('System in safe mode')); score -= 15 } else f.push(pass('Not in safe mode'))
  if (safety.degraded.active) { f.push(fail(`DEGRADED: ${safety.degraded.reason}`)); score -= 30 } else f.push(pass('Not degraded'))
  if (safety.killSwitches.length > 0) { f.push(warn(`Kill switches: ${safety.killSwitches.join(', ')}`)); score -= safety.killSwitches.length * 10 } else f.push(pass('No kill switches'))
  const open = safety.circuits.filter((c) => c.state === 'open')
  if (open.length > 0) { f.push(fail(`${open.length} circuit(s) open: ${open.map((c) => c.module).join(', ')}`)); score -= open.length * 15 } else f.push(pass('All circuits closed'))
  if (orch.overall === 'critical') { f.push(fail(`Orchestrator CRITICAL: ${orch.failedEngines.join(', ')}`)); score -= 20 } else if (orch.overall === 'degraded') { f.push(warn(`Orchestrator degraded`)); score -= 10 } else f.push(pass(`${orch.running} engines running`))
  return dim('runtime_safety', 'Runtime Safety', Math.max(0, score), f)
}

function auditTenantIsolation(): AuditDimension {
  const f: AuditFinding[] = []; let score = 100
  const withReset    = STORES.filter((s) => s.isTenantScoped && s.hasReset)
  const withoutReset = STORES.filter((s) => s.isTenantScoped && !s.hasReset)
  if (withReset.length > 0) f.push(pass(`${withReset.length} tenant stores have reset()`))
  if (withoutReset.length > 0) { f.push(warn(`${withoutReset.length} tenant stores missing reset(): ${withoutReset.map((s) => s.id).join(', ')}`)); score -= withoutReset.length * 8 }
  const integrity = runIntegrityCheck()
  const intLevel = integrity.violations.some((v) => v.level === 'error') ? 'error' : integrity.violations.some((v) => v.level === 'warning') ? 'warning' : 'ok'
  if (intLevel === 'error') { f.push(fail(`Integrity errors: ${integrity.violations.slice(0,3).map((v) => v.message).join('; ')}`)); score -= 20 }
  else if (intLevel === 'warning') { f.push(warn(`${integrity.violations.length} integrity warning(s)`)); score -= 10 }
  else f.push(pass('Store integrity clean'))
  return dim('tenant_isolation', 'Tenant Isolation', Math.max(0, score), f)
}

function auditMemoryStability(): AuditDimension {
  const f: AuditFinding[] = []; let score = 100
  const leak = getLastLeakReport(); const health = getLastHealthSnapshot()
  if (!leak) { f.push(warn('No leak report — detector may not be running')); score -= 15 }
  else {
    const crits = leak.leaks.filter((e) => e.severity === 'critical')
    const warns = leak.leaks.filter((e) => e.severity === 'warning')
    if (crits.length > 0) { f.push(fail(`${crits.length} critical leak(s)`)); score -= crits.length * 20 } else f.push(pass('No critical leaks'))
    if (warns.length > 0) { f.push(warn(`${warns.length} leak warning(s)`)); score -= warns.length * 5 }
  }
  if (health) {
    if (health.memory.status === 'critical') { f.push(fail(`Heap critical: ${health.memory.heapUsedMB.toFixed(0)}MB`)); score -= 25 }
    else if (health.memory.status === 'warning') { f.push(warn(`Heap elevated: ${health.memory.heapUsedMB.toFixed(0)}MB`)); score -= 10 }
    else f.push(pass(`Heap OK: ${health.memory.heapUsedMB.toFixed(0)}MB`))
  } else { f.push(warn('No health snapshot')); score -= 10 }
  return dim('memory_stability', 'Memory Stability', Math.max(0, score), f)
}

function auditChannelIntegrity(): AuditDimension {
  const f: AuditFinding[] = []; let score = 100
  const snap = getChannelSnapshot(); const zombies = snap.zombies
  f.push(pass(`${snap.openChannels.length} channels open`))
  if (zombies.length > 0) { f.push(fail(`${zombies.length} zombie(s): ${zombies.map((z) => z.name).join(', ')}`)); score -= zombies.length * 20 } else f.push(pass('No zombies'))
  return dim('channel_integrity', 'Channel Integrity', Math.max(0, score), f)
}

function auditTVStability(): AuditDimension {
  const f: AuditFinding[] = []; let score = 100
  const fps = useTVHealthStore.getState().fps
  if (fps > 0) {
    if (fps < 20) { f.push(fail(`FPS critical: ${fps.toFixed(0)}`)); score -= 30 }
    else if (fps < 30) { f.push(warn(`FPS low: ${fps.toFixed(0)}`)); score -= 10 }
    else f.push(pass(`FPS OK: ${fps.toFixed(0)}`))
  } else { f.push(warn('FPS not measured')); score -= 10 }
  const stalls = (useTVHealthStore.getState() as unknown as Record<string, unknown>)['stallCount']
  if (typeof stalls === 'number') {
    if (stalls > 10) { f.push(fail(`High stalls: ${stalls}`)); score -= 20 }
    else if (stalls > 3) { f.push(warn(`Stalls elevated: ${stalls}`)); score -= 10 }
    else f.push(pass(`Stalls OK: ${stalls}`))
  }
  return dim('tv_stability', 'TV Stability', Math.max(0, score), f)
}

function auditObservabilityHealth(): AuditDimension {
  const f: AuditFinding[] = []; let score = 100
  const health = getLastHealthSnapshot()
  if (!health) { f.push(fail('No health snapshot — observability down')); score -= 40 }
  else { f.push(pass(`Health monitor: ${health.overall}`)); if (health.observability.status === 'warning') { f.push(warn('Obs buffer elevated')); score -= 10 } }
  if (!getLastLeakReport()) { f.push(warn('Leak detector not reporting')); score -= 15 } else f.push(pass('Leak detector active'))
  const topo = runtimeTopologyReport()
  if (topo.potentialOrphans.length > 3) { f.push(warn(`${topo.potentialOrphans.length} architectural orphans`)); score -= 10 } else f.push(pass('Topology clean'))
  return dim('observability', 'Observability Health', Math.max(0, score), f)
}

function auditRouteProtection(): AuditDimension {
  const f: AuditFinding[] = []; let score = 100
  const enterpriseRoutes = ['/twin', '/vision/cameras', '/executive', '/executive/war-room']
  f.push(pass(`${ROUTES.filter((r) => r.isProtected).length} protected routes`))
  for (const path of enterpriseRoutes) {
    const route = ROUTES.find((r) => r.path === path)
    if (!route) { f.push(warn(`Route "${path}" not in manifest`)); score -= 5 }
    else if (!route.requiredRole) { f.push(fail(`"${path}" has no requiredRole`)); score -= 15 }
    else f.push(pass(`${path} -> ${route.requiredRole}`))
  }
  return dim('route_protection', 'Route Protection', Math.max(0, score), f)
}

function auditDeployReadiness(): AuditDimension {
  const f: AuditFinding[] = []; let score = 100
  const r = validateEnterpriseDeployment()
  r.blockers.forEach((b) => { f.push(fail(`[${b.category}] ${b.name}: ${b.message}`)); score -= 20 })
  r.checks.filter((c) => c.severity === 'warn').forEach((w) => { f.push(warn(`[${w.category}] ${w.name}: ${w.message}`)); score -= 5 })
  if (r.deployReady) f.push(pass(`Deploy validated (${r.summary.pass} checks pass)`))
  return dim('deploy_readiness', 'Deploy Readiness', Math.max(0, score), f)
}

function computeLevel(score: number, hasFails: boolean): CertificationLevel {
  if (hasFails || score < 40) return 'DEV'
  if (score < 65) return 'PILOT READY'
  if (score < 85) return 'ENTERPRISE READY'
  return 'MISSION CRITICAL READY'
}

export function enterpriseCertificationReport(): EnterpriseCertificationReport {
  const dimensions = [auditRuntimeSafety(), auditTenantIsolation(), auditMemoryStability(), auditChannelIntegrity(), auditTVStability(), auditObservabilityHealth(), auditRouteProtection(), auditDeployReadiness()]
  const weights: Record<string, number> = { runtime_safety: 20, tenant_isolation: 15, memory_stability: 15, channel_integrity: 10, tv_stability: 10, observability: 10, route_protection: 10, deploy_readiness: 10 }
  let ws = 0; let tw = 0
  for (const d of dimensions) { const w = weights[d.id] ?? 10; ws += d.score * w; tw += w }
  const overallScore = Math.round(ws / tw)
  const allF = dimensions.flatMap((d) => d.findings)
  const critical = allF.filter((f) => f.severity === 'fail')
  const warnings = allF.filter((f) => f.severity === 'warn')
  const certificationLevel = computeLevel(overallScore, critical.length > 0)
  const lvlEmoji: Record<CertificationLevel, string> = { 'DEV': 'RED', 'PILOT READY': 'YELLOW', 'ENTERPRISE READY': 'GREEN', 'MISSION CRITICAL READY': 'STAR' }
  return { timestamp: new Date().toISOString(), overallScore, certificationLevel, dimensions, criticalFindings: critical, warnings, summary: `[${lvlEmoji[certificationLevel]}] ${certificationLevel} | Score: ${overallScore}/100 | Critical: ${critical.length} | Warn: ${warnings.length}` }
}

export function printCertificationReport(report: EnterpriseCertificationReport): void {
  console.groupCollapsed(`[Canteiro] Certification: ${report.certificationLevel} (${report.overallScore}/100)`)
  console.log(report.summary)
  for (const d of report.dimensions) {
    console.groupCollapsed(`${d.level === 'pass' ? 'OK' : d.level === 'warn' ? 'WARN' : 'FAIL'} ${d.name}: ${d.score}/100`)
    d.findings.forEach((f) => { if (f.severity === 'fail') console.error(`  X ${f.message}`); else if (f.severity === 'warn') console.warn(`  ! ${f.message}`); else console.log(`  v ${f.message}`) })
    console.groupEnd()
  }
  console.groupEnd()
}
